import { useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect, useRef, ChangeEvent } from 'react';
import { useSelector } from 'react-redux';

import styles from './styles.module.css';
import {
  canNavigateDateRangeForward,
  DATE_RANGE_DIRECTION_BACK,
  DATE_RANGE_DIRECTION_FORWARD,
  ensureStartBeforeEnd,
  formatHistoryDateTimeLocal,
  formatHistoryUiDate,
  getEndDateFromStart,
  getFetchDateRange,
  getInitialDateRange,
  getShiftedDateRange,
  getStartDateFromEnd,
  normalizeRangeMillis
} from './utils/summaryHistoryInterval';
import DownloadIcon from '@/assets/icons/download2.svg?react';
import RefreshIcon from '@/assets/icons/refresh.svg?react';
import DateInput from '@/components/Form/DateInput';
import Loader from '@/components/Form/Loader';
import SearchInputWithList from '@/components/Form/SearchInputWithList';
import Select from '@/components/Form/Select';
import PowerChartDelta from '@/components/PowerChartDelta';
import { transformHistoryData } from '@/containers/LocationViewContainer/utils';
import { useToast } from '@/context/ToastContext';
import {
  useExportSummaryHistory,
  useGetHistoryTags
} from '@/hooks/services/Turbines';
import {
  useTurbineHistorySeries,
  TurbineHistorySeriesMetric,
  TurbineSeriesError
} from '@/hooks/services/Turbines/useTurbineHistorySeries';
import { installedProductsKeys } from '@/solaris-lens-common-lib/src/constants/queryKeys/InstalledProducts';
import {
  getDayjs,
  US_LONG_AM_PM_UI,
  getDateToSave,
  getShortUSFormatDateYearInit
} from '@/solaris-lens-common-lib/src/utils/dates';
import {
  getIntervalOptions,
  getMaxRangeMillis
} from '@/solaris-lens-common-lib/src/utils/turbinesHistory';
import { exportDocument } from '@/solaris-lens-common-lib-web/src/utils/exportDocument';
import { RootState } from '@/store';

const DEFAULT_INTERVAL = '1m';
const initialDateRange = getInitialDateRange();
const initialUpdatedData: UpdatedDataProps = {
  startDate: initialDateRange.startDate,
  endDate: initialDateRange.endDate,
  interval: DEFAULT_INTERVAL,
  scope: '',
  metric: '',
  applyDateFormat: false,
  category: '',
  ids: undefined
};

export type UpdatedDataProps = {
  startDate: String;
  endDate: String;
  interval: string;
  scope: string;
  metric: string;
  applyDateFormat: boolean;
  category: string;
  ids?: string;
};

export type HistoryTag = {
  label: string;
  attrKey: string;
  scope: string;
  type: string;
};

interface SummaryHistoryProps {
  canModifyFilters?: boolean;
}

export default function SummaryHistory({
  canModifyFilters = true
}: SummaryHistoryProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const turbineId = useSelector(
    (state: RootState) => state?.installedProducts?.turbineId
  );

  const [shouldRefetch, setShouldRefetch] = useState(false);
  const hasAppliedInitialIntervalRef = useRef(false);
  const [chartMetrics, setChartMetrics] = useState<
    TurbineHistorySeriesMetric[]
  >([
    {
      category: 'measure',
      id: 'genTotalKwAnalog',
      scope: 'generator'
    }
  ]);
  const [historyTagsState, setHistoryTagsState] = useState<HistoryTag[]>([]);
  const [updatedData, setUpdatedData] = useState<UpdatedDataProps>({
    ...initialUpdatedData,
    ids: turbineId
  });
  const [onFetchHistoryData, setOnFetchHistoryData] = useState({
    ...updatedData,
    startDate: getDateToSave(updatedData.startDate),
    endDate: getDateToSave(updatedData.endDate),
    metrics: chartMetrics
  });

  const { data: historyTags, isLoading: loadingHistoryTags } =
    useGetHistoryTags({
      turbineSerialNumber: turbineId,
      useQueryOptions: {
        staleTime: Infinity,
        retry: false
      }
    });
  useEffect(() => {
    if (historyTags) {
      setHistoryTagsState(historyTags.tags);
      setChartMetrics(prev =>
        prev.map(metric => {
          if (metric.label) return metric;
          const tag = historyTags.tags.find(
            (t: HistoryTag) => t.attrKey === metric.id
          );
          return tag ? { ...metric, label: tag.label } : metric;
        })
      );
    }
  }, [historyTags]);

  const { isLoading: loadingExportHistory, refetch: fetchExport } =
    useExportSummaryHistory({
      params: {
        turbineId: turbineId,
        startDate: getDateToSave(updatedData.startDate),
        endDate: getDateToSave(updatedData.endDate),
        interval: updatedData.interval,
        applyDateFormat: false
      },
      body: { metrics: chartMetrics } as {
        metrics: TurbineHistorySeriesMetric[];
      },
      useQueryOptions: {
        retry: false,
        staleTime: Infinity
      }
    });

  const { intervals } = historyTags ?? {};
  const intervalsOptions = getIntervalOptions(intervals ?? []);

  const {
    data: rawChartData,
    isLoading: isLoadingChart,
    refetch: fetchHistorySeries
  } = useTurbineHistorySeries(
    {
      turbineId: turbineId || '',
      startDate: onFetchHistoryData.startDate,
      endDate: String(onFetchHistoryData.endDate),
      interval: onFetchHistoryData.interval,
      applyDateFormat: onFetchHistoryData.applyDateFormat,
      metrics: onFetchHistoryData.metrics
    },
    { useQueryOptions: { staleTime: Infinity, enabled: false } }
  );

  useEffect(() => {
    hasAppliedInitialIntervalRef.current = false;
  }, [turbineId]);

  useEffect(() => {
    if (
      loadingHistoryTags ||
      !historyTags ||
      hasAppliedInitialIntervalRef.current
    ) {
      return;
    }

    hasAppliedInitialIntervalRef.current = true;

    const maxRangeMillis = normalizeRangeMillis(
      getMaxRangeMillis(intervals, DEFAULT_INTERVAL)
    );
    const { startDate, endDate } = getInitialDateRange(maxRangeMillis);
    const fetchDates = getFetchDateRange(startDate, endDate, maxRangeMillis);

    setUpdatedData(prev => ({
      ...prev,
      startDate,
      endDate,
      interval: DEFAULT_INTERVAL,
      ids: turbineId
    }));
    setOnFetchHistoryData(prev => ({
      ...prev,
      ...fetchDates,
      interval: DEFAULT_INTERVAL,
      ids: turbineId
    }));
    setShouldRefetch(true);
  }, [loadingHistoryTags, historyTags, intervals, turbineId]);

  useEffect(() => {
    if (shouldRefetch) {
      fetchHistorySeries();
      setShouldRefetch(false);
    }
  }, [onFetchHistoryData, shouldRefetch]);

  const chartData = useMemo(() => {
    if (!rawChartData?.data) return [];
    const transformed = transformHistoryData(rawChartData.data, false);
    return transformed.length ? transformed : [];
  }, [rawChartData]);

  const errors = useMemo((): TurbineSeriesError[] => {
    if (!rawChartData?.errors) return [];
    return rawChartData.errors as TurbineSeriesError[];
  }, [rawChartData]);

  const interval = intervalsOptions?.find(
    (option: { value: string }) => option.value === updatedData?.interval
  )?.maxRangeMillis;

  const refetchHistoryWithMetrics = (metrics: TurbineHistorySeriesMetric[]) => {
    queryClient.invalidateQueries({ queryKey: installedProductsKeys.all });
    const fetchDates = getFetchDateRange(
      String(updatedData.startDate),
      String(updatedData.endDate),
      interval
    );
    setOnFetchHistoryData({
      ...updatedData,
      ...fetchDates,
      metrics
    });
    setShouldRefetch(true);
  };

  const onRefresh = () => refetchHistoryWithMetrics(chartMetrics);

  const onSelectChange = (id: string, data: { value: string }) => {
    const newInterval = id === 'interval' ? data.value : updatedData.interval;
    const rangeMillis = normalizeRangeMillis(
      getMaxRangeMillis(intervals, newInterval)
    );
    const nextStartDate = getStartDateFromEnd(
      String(updatedData.endDate),
      rangeMillis
    );
    const nextRange = ensureStartBeforeEnd(
      nextStartDate,
      String(updatedData.endDate),
      rangeMillis
    );

    setUpdatedData({
      ...updatedData,
      [id]: data.value,
      startDate: nextRange.startDate,
      endDate: nextRange.endDate
    });
  };

  const onDateRangeNavigate = (direction: number) => {
    const nextDateRange = getShiftedDateRange(
      String(updatedData.startDate),
      String(updatedData.endDate),
      direction,
      interval
    );
    if (!nextDateRange) return;

    const nextUpdatedData = {
      ...updatedData,
      startDate: nextDateRange.startDate,
      endDate: nextDateRange.endDate
    };
    const fetchDates = getFetchDateRange(
      nextDateRange.startDate,
      nextDateRange.endDate,
      interval
    );

    setUpdatedData(nextUpdatedData);
    queryClient.invalidateQueries({ queryKey: installedProductsKeys.all });
    setOnFetchHistoryData({
      ...nextUpdatedData,
      ...fetchDates,
      metrics: chartMetrics
    });
    setShouldRefetch(true);
  };

  const canNavigateRangeForward = canNavigateDateRangeForward(
    String(updatedData.endDate)
  );

  const selectedIntervalLabel =
    intervalsOptions?.find(
      (option: { value: string; label: string }) =>
        option.value === updatedData.interval
    )?.label ?? updatedData.interval;

  const onSelectDate = (id: string, date: {}) => {
    const dateToSave = formatHistoryUiDate(String(date));
    const rangeMillis = normalizeRangeMillis(interval);

    if (id === 'startDate') {
      const nextRange = ensureStartBeforeEnd(
        dateToSave,
        getEndDateFromStart(dateToSave, rangeMillis),
        rangeMillis
      );
      setUpdatedData({
        ...updatedData,
        startDate: nextRange.startDate,
        endDate: nextRange.endDate
      });
      return;
    }

    const nextRange = ensureStartBeforeEnd(
      getStartDateFromEnd(dateToSave, rangeMillis),
      dateToSave,
      rangeMillis
    );
    setUpdatedData({
      ...updatedData,
      startDate: nextRange.startDate,
      endDate: nextRange.endDate
    });
  };

  const [searchTag, setSearchTag] = useState('');
  const [debouncedSearchTag, setDebouncedSearchTag] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTag(searchTag), 300);
    return () => clearTimeout(timer);
  }, [searchTag]);

  const filteredTags = useMemo(() => {
    const tags = historyTagsState ?? [];
    if (!debouncedSearchTag) return [];
    return tags
      .filter(tag => {
        const q = debouncedSearchTag.toLowerCase();
        return (
          tag.attrKey.toLowerCase().includes(q) ||
          tag.label?.toLowerCase().includes(q)
        );
      })
      .map(tag => ({ value: tag.attrKey, label: tag.label }));
  }, [historyTagsState, debouncedSearchTag]);

  const handleSearchTagChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTag(e.target.value);
  };

  const onExportHistory = async () => {
    const { data } = await fetchExport();
    if (!data) return;
    const formattedStartDate = getShortUSFormatDateYearInit(
      updatedData.startDate
    );
    exportDocument({
      file: data as unknown as null | undefined,
      fileName: `${turbineId}-history-${formattedStartDate}`,
      extension: 'csv'
    });
  };

  const onItemAction = (item: { value: string; label: string }) => {
    const tag = historyTagsState.find(t => t.attrKey === item.value);
    if (!tag) return;
    if (chartMetrics.length >= 20) {
      showToast('You can only add up to 20 metrics', {
        type: 'error',
        autoClose: 3000
      });
      return;
    }
    const nextMetrics = [
      ...chartMetrics,
      {
        id: tag.attrKey,
        scope: tag.scope,
        category: tag.type,
        label: tag.label
      }
    ];
    setChartMetrics(nextMetrics);
    setHistoryTagsState(
      historyTagsState.filter(t => t.attrKey !== tag.attrKey)
    );
    refetchHistoryWithMetrics(nextMetrics);
  };

  const onRemoveMetric = (metric: TurbineHistorySeriesMetric) => {
    const nextMetrics = chartMetrics.filter(m => m.id !== metric.id);
    setChartMetrics(nextMetrics);
    const originalTag = historyTags?.tags?.find(
      (t: HistoryTag) => t.attrKey === metric.id
    );
    if (originalTag) setHistoryTagsState(prev => [...prev, originalTag]);
    refetchHistoryWithMetrics(nextMetrics);
  };

  return (
    <div className={styles.chartSection}>
      {canModifyFilters && (
        <div className={styles.chartControls}>
          <div className={styles.controlGroup}>
            <DateInput
              label="START"
              value={formatHistoryDateTimeLocal(String(updatedData.startDate))}
              onChange={value => onSelectDate('startDate', value)}
              type="datetime-local"
              variant="compact"
              max={formatHistoryDateTimeLocal(
                getDayjs().format(US_LONG_AM_PM_UI)
              )}
            />
          </div>
          <div className={styles.controlGroup}>
            <DateInput
              label="END"
              value={formatHistoryDateTimeLocal(String(updatedData.endDate))}
              onChange={value => onSelectDate('endDate', value)}
              type="datetime-local"
              variant="compact"
              max={formatHistoryDateTimeLocal(
                getEndDateFromStart(
                  String(updatedData.startDate),
                  normalizeRangeMillis(interval)
                )
              )}
              min={formatHistoryDateTimeLocal(String(updatedData.startDate))}
            />
          </div>
          <div className={styles.controlGroup}>
            <Select
              label="INTERVAL"
              value={updatedData.interval}
              onChange={value => onSelectChange('interval', { value })}
              options={intervalsOptions ?? []}
              customClass={styles.wrapperSelectClass}
              customLabelClass={styles.controlLabel}
              customSelectClass={styles.selectClass}
              customSelectWrapperClass={styles.selectWrapperClass}
            />
          </div>
          <button className={styles.actionButton} type="button">
            {loadingHistoryTags ? (
              <Loader size="medium" />
            ) : (
              <RefreshIcon className={styles.refreshIcon} onClick={onRefresh} />
            )}
          </button>
          <div className={styles.controlGroup}>
            <SearchInputWithList
              value={searchTag}
              onChange={handleSearchTagChange}
              resetSearch={() => setSearchTag('')}
              showLoader={!!searchTag && searchTag !== debouncedSearchTag}
              placeholder="Search by tag"
              customClass={styles.searchTagInput}
              onItemAction={onItemAction}
              list={filteredTags}
            />
          </div>

          <div className={styles.controlActions}>
            <button className={styles.actionButton} type="button">
              {loadingExportHistory ? (
                <Loader size="medium" />
              ) : (
                <DownloadIcon
                  className={styles.downloadIcon}
                  onClick={onExportHistory}
                />
              )}
            </button>
          </div>
        </div>
      )}

      <div className={styles.chartContainer}>
        <PowerChartDelta
          data={chartData}
          errors={errors}
          chartMetrics={chartMetrics}
          isLoading={isLoadingChart}
          onRemoveMetric={onRemoveMetric}
          onNavigateBack={
            canModifyFilters
              ? () => onDateRangeNavigate(DATE_RANGE_DIRECTION_BACK)
              : undefined
          }
          onNavigateForward={
            canModifyFilters
              ? () => onDateRangeNavigate(DATE_RANGE_DIRECTION_FORWARD)
              : undefined
          }
          canNavigateForward={canNavigateRangeForward}
          navigateBackLabel={`Previous ${selectedIntervalLabel}`}
          navigateForwardLabel={`Next ${selectedIntervalLabel}`}
        />
      </div>
    </div>
  );
}
